'use client'

import { TagsNSFW, TagsSFW } from '@/constant/tags'
import { HomeIconFinish, TagEventIcon, TagNewIcon } from '@/icons'
import { HomeFilterContentType } from '@/types/character'
import { TagID } from '@/types/tag'
import clsx from 'clsx'
import useI18n from 'Hooks/useI18n'
import { FC, useCallback, useEffect, useState } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import { clickedTagsWithBadgeState, homeNSFWState } from 'Store/home'
import TagDisplayLabel from '@/components/common/TagDisplayLabel'
import { shouldShowTagBadgeDot } from '@/utils/tagDisplay'

type Props = {
  open: boolean
  onClose: () => void
  selectedTagIds: TagID[]
  selectedContentTypes: HomeFilterContentType[]
  onSelectTags: (tagIds: TagID[]) => void
  onSelectContentTypes: (contentTypes: HomeFilterContentType[]) => void
}

const HomeFilterPopover: FC<Props> = ({
  open,
  onClose,
  selectedTagIds,
  selectedContentTypes,
  onSelectTags,
  onSelectContentTypes,
}) => {
  const { t } = useI18n()
  const nsfw = useRecoilValue(homeNSFWState)
  const [clickedTagsWithBadge, setClickedTagsWithBadge] = useRecoilState(
    clickedTagsWithBadgeState
  )
  const [currentTagIds, setCurrentTagIds] = useState<TagID[]>([])
  const [currentContentTypes, setCurrentContentTypes] = useState<
    HomeFilterContentType[]
  >([])

  useEffect(() => {
    if (!open) return
    setCurrentTagIds(selectedTagIds)
    setCurrentContentTypes(selectedContentTypes)
  }, [open, selectedTagIds, selectedContentTypes])

  const handleTagSelect = useCallback(
    (id: TagID) => {
      setCurrentTagIds((prev) => {
        if (prev.includes(id)) {
          return prev.filter((item) => item !== id)
        }

        const newTags = [...prev, id]
        const filterTags = nsfw ? TagsNSFW : TagsSFW
        const selectedTag = filterTags.find((tag) => tag.id === id)

        if (
          selectedTag &&
          shouldShowTagBadgeDot(selectedTag) &&
          !clickedTagsWithBadge.includes(selectedTag.tag)
        ) {
          setClickedTagsWithBadge([...clickedTagsWithBadge, selectedTag.tag])
        }

        return newTags
      })
    },
    [clickedTagsWithBadge, nsfw, setClickedTagsWithBadge]
  )

  const handleReset = useCallback(() => {
    setCurrentTagIds([])
    setCurrentContentTypes([])
  }, [])

  const handleFinish = useCallback(() => {
    onSelectTags(currentTagIds)
    onSelectContentTypes(currentContentTypes)
    onClose()
  }, [
    currentContentTypes,
    currentTagIds,
    onClose,
    onSelectContentTypes,
    onSelectTags,
  ])

  if (!open) return null

  return (
    <div className="absolute right-0 top-[42px] z-30 w-[420px] rounded-2xl border border-white/10 bg-[#2A2124]/95 p-4 shadow-[0_10px_30px_rgba(0,0,0,0.35)] backdrop-blur-md">
      <header className="mb-4 flex items-center gap-2 text-secondary-text">
        <span className="text-base text-white">{t('Filter')}</span>
        <span className="text-white/40">|</span>
        <button
          type="button"
          onClick={handleReset}
          className={clsx(
            'text-sm',
            currentTagIds.length || currentContentTypes.length
              ? 'text-white'
              : 'text-secondary-text'
          )}
        >
          {t('Reset')}
        </button>
        <button type="button" onClick={handleFinish} className="ml-auto">
          <HomeIconFinish />
        </button>
      </header>

      <section>
        <div className="mb-2 text-sm text-white">{`${t('Tags')} :`}</div>
        <div className="max-h-[260px] overflow-y-auto">
          <div className="flex flex-wrap gap-2">
            {(nsfw ? TagsNSFW : TagsSFW).map((tagItem) => (
              <button
                type="button"
                key={tagItem.id}
                className={clsx(
                  'relative inline-flex h-[34px] items-center rounded-full px-4 text-sm transition duration-300',
                  currentTagIds.includes(tagItem.id)
                    ? 'bg-white/20 text-white'
                    : 'bg-secondary-bg text-[#806E74]'
                )}
                onClick={() => handleTagSelect(tagItem.id)}
              >
                {tagItem.isNew && (
                  <span className="absolute right-[-8px] top-[-6px]">
                    <TagNewIcon />
                  </span>
                )}
                {tagItem.isEvent && (
                  <span className="absolute right-[-8px] top-[-6px]">
                    <TagEventIcon />
                  </span>
                )}
                {shouldShowTagBadgeDot(tagItem) &&
                  !clickedTagsWithBadge.includes(tagItem.tag) && (
                    <span className="absolute right-[2px] top-[2px] h-2 w-2 rounded-full bg-[#FF3B30]" />
                  )}
                <TagDisplayLabel tag={tagItem} iconSize={18} />
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

export default HomeFilterPopover
